import { useContext, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { CustomButton } from "src/design/CustomButton";
import { InfoCard } from "src/design/InfoCard";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";
import {
  SubscriptionStatus,
  getSubscriptionStatus,
} from "src/business/subscription/subscriptionStatus";
import { useAdminSubscriptions } from "src/data/admin/queries/subscription";
import { AdminRestaurantSubscriptionDto } from "src/data/admin/types/subscription";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import {
  GrantRestaurantOption,
  GrantSubscriptionDialog,
} from "./components/GrantSubscriptionDialog";
import { EditSubscriptionDialog } from "./components/EditSubscriptionDialog";
import { RenewSubscriptionDialog } from "./components/RenewSubscriptionDialog";
import { RevokeSubscriptionDialog } from "./components/RevokeSubscriptionDialog";
import {
  SubscriptionRow,
  SubscriptionsTable,
} from "./components/SubscriptionsTable";
import { UnsubscribedRestaurantsTable } from "./components/UnsubscribedRestaurantsTable";

type StatusFilter = "all" | SubscriptionStatus;

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  all: "All statuses",
  [SubscriptionStatus.Active]: "Active",
  [SubscriptionStatus.Grace]: "In grace period",
  [SubscriptionStatus.Expired]: "Expired",
  [SubscriptionStatus.Perpetual]: "Never expires",
};

const matchesSearch = (
  search: string,
  name: string,
  id: string,
  city: string,
) => {
  const term = search.trim().toLowerCase();
  if (!term) return true;

  return [name, id, city].some((field) => field.toLowerCase().includes(term));
};

type Snack = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

const Subscriptions = () => {
  const { restaurants, isLoading: restaurantsLoading } =
    useContext(RestaurantContext);
  const { data: subscriptions, isLoading, isError } = useAdminSubscriptions();
  const { data: catalog } = useGetSubscriptionTiers();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [grantTarget, setGrantTarget] = useState<GrantRestaurantOption | null>(
    null,
  );
  const [grantOpen, setGrantOpen] = useState(false);
  const [editTarget, setEditTarget] =
    useState<AdminRestaurantSubscriptionDto | null>(null);
  const [renewTarget, setRenewTarget] =
    useState<AdminRestaurantSubscriptionDto | null>(null);
  const [revokeTarget, setRevokeTarget] =
    useState<AdminRestaurantSubscriptionDto | null>(null);
  const [snack, setSnack] = useState<Snack>({
    open: false,
    message: "",
    severity: "success",
  });

  const tiers = useMemo(
    () => (catalog ?? []).map((plan) => plan.name),
    [catalog],
  );

  const cityByRestaurantId = useMemo(
    () =>
      new Map(
        restaurants.map((restaurant) => [restaurant.id, restaurant.city]),
      ),
    [restaurants],
  );

  const filteredSubscriptions = useMemo(
    (): SubscriptionRow[] =>
      (subscriptions ?? [])
        .map((subscription) => ({
          ...subscription,
          city: cityByRestaurantId.get(subscription.restaurantId),
        }))
        .filter(
          (subscription) =>
            matchesSearch(
              search,
              subscription.restaurantName,
              subscription.restaurantId,
              subscription.city ?? "",
            ) &&
            (statusFilter === "all" ||
              getSubscriptionStatus(subscription) === statusFilter),
        ),
    [subscriptions, cityByRestaurantId, search, statusFilter],
  );

  // Restaurants with no record at all: not subscribed, so invisible to staff.
  const unsubscribedRestaurants = useMemo(() => {
    const subscribedIds = new Set(
      (subscriptions ?? []).map((subscription) => subscription.restaurantId),
    );
    return restaurants
      .filter((restaurant) => !subscribedIds.has(restaurant.id))
      .map((restaurant) => ({
        id: restaurant.id,
        name: restaurant.name,
        city: restaurant.city,
      }));
  }, [restaurants, subscriptions]);

  const filteredUnsubscribed = useMemo(
    () =>
      unsubscribedRestaurants.filter((restaurant) =>
        matchesSearch(search, restaurant.name, restaurant.id, restaurant.city),
      ),
    [unsubscribedRestaurants, search],
  );

  const showSuccess = (message: string) =>
    setSnack({ open: true, message, severity: "success" });
  const showError = (message: string) =>
    setSnack({ open: true, message, severity: "error" });

  const openGrant = (restaurant: GrantRestaurantOption | null) => {
    setGrantTarget(restaurant);
    setGrantOpen(true);
  };

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: colorTextPrimary }}
            >
              Restaurant Subscriptions
            </Typography>
            <Typography variant="body2" sx={{ color: colorTextMuted }}>
              A subscription gates the whole restaurateur dashboard. Access ends
              3 days after the expiration date — revoking cuts it immediately.
            </Typography>
          </Box>
          <CustomButton
            color="black"
            startIcon={<AddIcon />}
            onClick={() => openGrant(null)}
          >
            Grant Subscription
          </CustomButton>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by restaurant name, city or id…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colorTextMuted, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(event.target.value as StatusFilter)
                }
              >
                {(Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).map(
                  (option) => (
                    <MenuItem key={option} value={option}>
                      {STATUS_FILTER_LABELS[option]}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {isLoading || restaurantsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isError ? (
          <Typography variant="body2" sx={{ color: colorTextError, py: 2 }}>
            Unable to load subscriptions.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InfoCard
                title={`Subscriptions (${filteredSubscriptions.length})`}
                icon={<WorkspacePremiumOutlinedIcon fontSize="small" />}
              >
                {filteredSubscriptions.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" sx={{ color: colorTextMuted }}>
                      {search || statusFilter !== "all"
                        ? "No subscription matches your filters."
                        : "No subscription on record yet."}
                    </Typography>
                  </Box>
                ) : (
                  <SubscriptionsTable
                    subscriptions={filteredSubscriptions}
                    onEdit={setEditTarget}
                    onRenew={setRenewTarget}
                    onRevoke={setRevokeTarget}
                  />
                )}
              </InfoCard>
            </Grid>

            {filteredUnsubscribed.length > 0 && (
              <Grid item xs={12}>
                <InfoCard
                  title={`Without a subscription (${filteredUnsubscribed.length})`}
                  subtitle="These restaurants are not listed for their own staff until a subscription is attached."
                  icon={<StorefrontOutlinedIcon fontSize="small" />}
                >
                  <UnsubscribedRestaurantsTable
                    restaurants={filteredUnsubscribed}
                    onGrant={openGrant}
                  />
                </InfoCard>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      <GrantSubscriptionDialog
        open={grantOpen}
        restaurantOptions={unsubscribedRestaurants}
        presetRestaurant={grantTarget}
        tiers={tiers}
        onClose={() => {
          setGrantOpen(false);
          setGrantTarget(null);
        }}
        onGranted={showSuccess}
        onError={showError}
      />

      <EditSubscriptionDialog
        subscription={editTarget}
        tiers={tiers}
        onClose={() => setEditTarget(null)}
        onUpdated={showSuccess}
        onError={showError}
      />

      <RenewSubscriptionDialog
        subscription={renewTarget}
        onClose={() => setRenewTarget(null)}
        onRenewed={showSuccess}
        onError={showError}
      />

      <RevokeSubscriptionDialog
        subscription={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={showSuccess}
        onError={showError}
      />

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((current) => ({ ...current, open: false }))}
      />
    </>
  );
};

export default Subscriptions;
